import { createAsyncThunk } from "@reduxjs/toolkit";
import { fetchProductsByIds } from "../helpers/ProductLookupHelper";
import DineInApiService, {
  toFriendlyDineInError,
  isKnownDineInErrorCode,
} from "../apiServices/DineInApiService";
import { ApiEnvelopeError } from "../helpers/ApiEnvelopeHelper";
import { ProductDetailsModal } from "../../interfaces/ProductObject";
import {
  OpenTableArg,
  JoinTableArg,
  SubmitDineInRoundPayload,
  AddDineInItemPayload,
} from "../../interfaces/DineInObject";
import { convertRawDineInSession } from "../converters/backendToUi/dineInConvertor";
import { convertRawOrder } from "../converters/backendToUi/orderConvertor";
import type { RawOrder } from "../../interfaces/OrderObject";

/**
 * A rejected dine-in call's payload: the plain error code as before, unless
 * the backend's errorInformation[0].additionalDetails included a hostName
 * (e.g. INVALID_OR_MISSING_SESSION_CODE names whoever opened the table) —
 * then it's this richer shape instead, so the slice/UI can address the host
 * by name. Kept as a union rather than always-an-object so every existing
 * `action.payload === SOME_CODE` check outside the one place that cares
 * about hostName doesn't need to change.
 */
export type DineInErrorPayload = string | { code: string; hostName: string };

function toDineInErrorPayload(error: ApiEnvelopeError): DineInErrorPayload {
  const hostName = error.additionalDetails?.hostName;
  return typeof hostName === "string" && hostName
    ? { code: error.errorCode, hostName }
    : error.errorCode;
}

/**
 * Resolves name/image/price for a set of productIds referenced by dine-in
 * round items — the dine-in schema only stores productId, not product
 * details, same separation CartDb.ts keeps between cart items and products.
 *
 * The envelope-unwrap quirk this used to spell out inline (getProductsByIds
 * lies about its return type) now lives in fetchProductsByIds, so the kitchen
 * board's thunk and this one can't drift apart on it.
 */
export const fetchDineInProducts = createAsyncThunk(
  "dineIn/fetchDineInProducts",
  async (productIds: string[], { rejectWithValue }) => {
    try {
      const products: ProductDetailsModal[] = await fetchProductsByIds(productIds);
      return products;
    } catch (e) {
      return rejectWithValue("Failed to load menu item details");
    }
  }
);

/**
 * Bootstrap call fired once when the dine-in route mounts. sessionCode is
 * whatever DineIn.tsx found in local storage for this table — null the very
 * first time anyone visits it.
 *
 *  - sessionCode: null,    success -> brand new session (reveal the code)
 *  - sessionCode: null,    10006   -> a session already exists (ask for the code)
 *  - sessionCode: <code>,  success -> returning member, straight back in
 *  - sessionCode: <code>,  10006   -> the stored code is stale/wrong (ask again)
 *
 * Rejects with the RAW errorCode (not friendly copy) so the slice can branch
 * on 10006 as a state, not an error — same convention as
 * OnboardingThunk.fetchCaptainSummary.
 */
export const openTable = createAsyncThunk(
  "dineIn/openTable",
  async (arg: OpenTableArg, { rejectWithValue }) => {
    try {
      const raw = await DineInApiService.openTable(arg);
      return convertRawDineInSession(raw);
    } catch (error) {
      // Only pass the raw code through for codes the slice actually branches
      // on (10005/10006) — anything else, including unwrapEnvelope's
      // synthetic HTTP_<status> fallback for a plain HTTP failure, goes
      // through toFriendlyDineInError so a raw status never reaches the UI.
      if (error instanceof ApiEnvelopeError && isKnownDineInErrorCode(error.errorCode)) {
        return rejectWithValue(toDineInErrorPayload(error));
      }
      return rejectWithValue(toFriendlyDineInError(error));
    }
  }
);

/**
 * Explicit join with a code the user typed in (DineInCodeEntry's submit).
 * Hits the same endpoint as openTable, kept as a separate thunk so the slice
 * can tell "the bootstrap call came back 10006" (show the code-entry screen)
 * apart from "the user's typed code came back 10006" (inline wrong-code
 * error on that same screen) without inspecting which phase is active.
 */
export const joinTable = createAsyncThunk(
  "dineIn/joinTable",
  async (arg: JoinTableArg, { rejectWithValue }) => {
    try {
      const raw = await DineInApiService.openTable(arg);
      return convertRawDineInSession(raw);
    } catch (error) {
      // Only pass the raw code through for codes the slice actually branches
      // on (10005/10006) — anything else, including unwrapEnvelope's
      // synthetic HTTP_<status> fallback for a plain HTTP failure, goes
      // through toFriendlyDineInError so a raw status never reaches the UI.
      if (error instanceof ApiEnvelopeError && isKnownDineInErrorCode(error.errorCode)) {
        return rejectWithValue(toDineInErrorPayload(error));
      }
      return rejectWithValue(toFriendlyDineInError(error));
    }
  }
);

/**
 * Closes out the current round — DineInActionBar's "Order" button, POST
 * PUT .../dine-in-rounds/{roundId} with status REQUESTED. Same envelope as
 * openTable/joinTable, so the slice reuses applySessionResponse for it too.
 */
export const submitRound = createAsyncThunk(
  "dineIn/submitRound",
  async (payload: SubmitDineInRoundPayload, { rejectWithValue }) => {
    try {
      const raw = await DineInApiService.submitRound(payload);
      return convertRawDineInSession(raw);
    } catch (error) {
      // Only pass the raw code through for codes the slice actually branches
      // on (10005/10006) — anything else, including unwrapEnvelope's
      // synthetic HTTP_<status> fallback for a plain HTTP failure, goes
      // through toFriendlyDineInError so a raw status never reaches the UI.
      if (error instanceof ApiEnvelopeError && isKnownDineInErrorCode(error.errorCode)) {
        return rejectWithValue(toDineInErrorPayload(error));
      }
      return rejectWithValue(toFriendlyDineInError(error));
    }
  }
);

/**
 * Adds one or more items to the session's current round in a single batched
 * call — DineInOrderFab's "Add to dine-in session" button on the Products
 * page (outside the dine-in route entirely), POST .../{sessionId}/items with
 * whatever was staged in the local collection cart. Same session envelope
 * response as openTable/joinTable/submitRound, so the slice reuses
 * applySessionResponse for it too.
 */
export const addDineInItems = createAsyncThunk(
  "dineIn/addDineInItems",
  async (payload: AddDineInItemPayload, { rejectWithValue }) => {
    try {
      const raw = await DineInApiService.addItems(payload);
      return convertRawDineInSession(raw);
    } catch (error) {
      // Only pass the raw code through for codes the slice actually branches
      // on (10005/10006) — anything else, including unwrapEnvelope's
      // synthetic HTTP_<status> fallback for a plain HTTP failure, goes
      // through toFriendlyDineInError so a raw status never reaches the UI.
      if (error instanceof ApiEnvelopeError && isKnownDineInErrorCode(error.errorCode)) {
        return rejectWithValue(toDineInErrorPayload(error));
      }
      return rejectWithValue(toFriendlyDineInError(error));
    }
  }
);

/**
 * Generates a table's bill, which closes the session. Used by both the diner
 * screen and the kitchen board (same endpoint), so billing changes in one
 * place. Resolves with the dine-in Order (carrying `bill`); rejects with
 * FRIENDLY copy. The dine-in slice stores the bill only when the session is the
 * one this client is sitting in, so a staff-side bill leaves it untouched.
 */
export const generateBill = createAsyncThunk(
  "dineIn/generateBill",
  async ({ sessionId }: { sessionId: string }, { rejectWithValue }) => {
    try {
      const raw = await DineInApiService.generateBill(sessionId);
      return convertRawOrder(raw as RawOrder);
    } catch (error) {
      return rejectWithValue(toFriendlyDineInError(error));
    }
  }
);

/**
 * Silent snapshot pull triggered by a DINE_IN_* live-update push (see the
 * live-updates plan — services/firebase/dineInPushRouter.ts, phase 3 wiring
 * still to come). Deliberately NOT on the toDineInErrorPayload/
 * friendlyDineInMessage error-surfacing path the other four thunks use: a
 * background refresh failing should never show the user an error or change
 * `phase`, just leave state as-is until the next event or a focus/visibility
 * resync retries. The rejectWithValue payload is unused by the slice's
 * .rejected case — it's a deliberate no-op, not an oversight.
 */
export const refreshDineInSession = createAsyncThunk(
  "dineIn/refreshDineInSession",
  async ({ sessionId }: { sessionId: string }, { rejectWithValue }) => {
    try {
      const raw = await DineInApiService.getSessionSnapshot(sessionId);
      return convertRawDineInSession(raw);
    } catch (error) {
      console.warn(
        "refreshDineInSession failed (will retry on next event/focus):",
        error
      );
      return rejectWithValue(null);
    }
  }
);

const DineInThunk = {
  fetchDineInProducts,
  openTable,
  joinTable,
  submitRound,
  addDineInItems,
  generateBill,
  refreshDineInSession,
};

export default DineInThunk;
